import { useState } from 'react';
import { format } from 'date-fns';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { CalendarIcon } from 'lucide-react';
import AutocompleteInput from './AutocompleteInput';
import ClientSelector from './ClientSelector';
import ContactSelector from './ContactSelector';
import { getHistoryItems } from '../../lib/defaultData';

export default function QuoteInfo({ quoteData, onUpdateField, onClientSelect, onContactSelect }) {
  const [dateOpen, setDateOpen] = useState(false);
  const projectHistory = getHistoryItems('history_projects');

  const dateValue = quoteData.date ? new Date(quoteData.date + 'T00:00:00') : new Date();

  const handleClientSelect = (client) => {
    // Auto-fill client-related fields from the selected client
    onUpdateField('client_name', client.company_name || '');
    onUpdateField('client_abn', client.abn || '');
    onUpdateField('client_address', client.address || '');
    onUpdateField('client_id', client.id || '');
    // Callback for parent
    onClientSelect?.(client);
  };

  const handleClientClear = () => {
    onUpdateField('client_id', '');
  };

  const handleContactSelect = (contact) => {
    // Auto-fill contact-related fields
    onUpdateField('contact_person', contact.full_name || '');
    // If the contact has an email/phone, we could fill those too
    onContactSelect?.(contact);
  };

  const handleContactClear = () => {
    // No separate contact_id field in quote — just clear the text
  };

  return (
    <div
      className="py-8 border-b"
      style={{ borderColor: 'rgba(201, 168, 76, 0.2)' }}
      data-testid="quote-info"
    >
      <h2 className="section-heading">Quotation</h2>
      <div className="grid grid-cols-2 gap-x-12 gap-y-4">
        {/* Quote No */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[#6b7280] mb-1 block">
            Quote No
          </label>
          <input
            value={quoteData.quote_no}
            onChange={e => onUpdateField('quote_no', e.target.value)}
            className="editable-field font-mono text-[#1a1a2e] font-semibold"
            placeholder="Q-YYMM-XXX"
            data-testid="quote-no-input"
          />
        </div>

        {/* Date */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[#6b7280] mb-1 block">
            Date
          </label>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <button
                className="editable-field flex items-center gap-2 text-left w-full"
                data-testid="quote-date-btn"
              >
                <CalendarIcon className="w-4 h-4 text-[#c9a84c]" />
                {format(dateValue, 'dd MMM yyyy')}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={dateValue}
                onSelect={(d) => {
                  if (d) {
                    onUpdateField('date', format(d, 'yyyy-MM-dd'));
                  }
                  setDateOpen(false);
                }}
                data-testid="quote-date-calendar"
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Client Name — now with ClientSelector */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[#6b7280] mb-1 block">
            Client Name
          </label>
          <ClientSelector
            value={quoteData.client_name}
            clientId={quoteData.client_id}
            onChange={v => onUpdateField('client_name', v)}
            onClientSelect={handleClientSelect}
            onClear={handleClientClear}
            placeholder="Search or type client name..."
            testId="client-name-input"
          />
        </div>

        {/* Client ABN */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[#6b7280] mb-1 block">
            Client ABN
          </label>
          <input
            value={quoteData.client_abn}
            onChange={e => onUpdateField('client_abn', e.target.value)}
            className="editable-field"
            placeholder="Auto-filled from client, or enter manually"
            data-testid="client-abn-input"
          />
        </div>

        {/* Contact Person — now with ContactSelector */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[#6b7280] mb-1 block">
            Contact Person
          </label>
          <ContactSelector
            value={quoteData.contact_person || ''}
            clientId={quoteData.client_id}
            onChange={v => onUpdateField('contact_person', v)}
            onContactSelect={handleContactSelect}
            onClear={handleContactClear}
            placeholder="Search or type contact name..."
            testId="contact-person-input"
          />
        </div>

        {/* Project Name */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[#6b7280] mb-1 block">
            Project Name
          </label>
          <AutocompleteInput
            value={quoteData.project_name}
            onChange={v => onUpdateField('project_name', v)}
            suggestions={projectHistory}
            placeholder="Enter project name"
            testId="project-name-input"
          />
        </div>

        {/* Revision */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[#6b7280] mb-1 block">
            Revision
          </label>
          <input
            value={quoteData.revision}
            onChange={e => onUpdateField('revision', e.target.value)}
            className="editable-field"
            placeholder="e.g. Final Design Approval Revision"
            data-testid="revision-input"
          />
        </div>

        {/* Project Address - full width */}
        <div className="col-span-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-[#6b7280] mb-1 block">
            Project Address
          </label>
          <textarea
            value={quoteData.project_address}
            onChange={e => onUpdateField('project_address', e.target.value)}
            className="editable-field resize-none"
            rows={2}
            placeholder="Enter project address"
            data-testid="project-address-input"
          />
        </div>

        {/* Client Business Address - full width */}
        <div className="col-span-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-[#6b7280] mb-1 block">
            Client Business Address
          </label>
          <textarea
            value={quoteData.client_address || ''}
            onChange={e => onUpdateField('client_address', e.target.value)}
            className="editable-field resize-none"
            rows={2}
            placeholder="Auto-filled from client, or enter manually"
            data-testid="client-address-input"
          />
        </div>
      </div>
    </div>
  );
}
